/** The canonical Canadian province/territory codes, in `Province` order. */
const PROVINCE_CODES = new Set([
    'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
]);
/**
 * Full province names (EN + FR) keyed in a *folded* form — lowercased, with
 * diacritics stripped and internal whitespace collapsed. Folding is what lets
 * one key cover the four spellings production actually stores for Quebec
 * ("Québec", "QUÉBEC", "Quebec", "québec") instead of four aliases each.
 */
const PROVINCE_NAMES = {
    alberta: 'AB',
    'british columbia': 'BC',
    'colombie-britannique': 'BC',
    manitoba: 'MB',
    'new brunswick': 'NB',
    'nouveau-brunswick': 'NB',
    'newfoundland and labrador': 'NL',
    'terre-neuve-et-labrador': 'NL',
    'nova scotia': 'NS',
    'nouvelle-ecosse': 'NS',
    'northwest territories': 'NT',
    'territoires du nord-ouest': 'NT',
    nunavut: 'NU',
    ontario: 'ON',
    'prince edward island': 'PE',
    'ile-du-prince-edouard': 'PE',
    quebec: 'QC',
    saskatchewan: 'SK',
    yukon: 'YT',
};
function fold(value) {
    return value
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\s+/g, ' ')
        .toLowerCase();
}
/**
 * Normalize a free-text province/state value to a Canadian province code.
 *
 * The `sites.state_province` column is free text and production holds every
 * shape a human can type: "QC", "Qc", "Québec", "QUÉBEC", "Quebec",
 * "QC - Québec", "ALBERTA", "COLOMBIE-BRITANNIQUE" — plus US states and the
 * occasional "Non-U.S. resident". Any caller that compares the raw value
 * against a list of 2-letter codes silently ignores the majority of real rows,
 * so place-of-supply callers MUST go through here.
 *
 * Returns `null` — never a guess — when the value is empty or is not a Canadian
 * province. In particular a 2-letter code is only accepted when it is an actual
 * province: "WA"/"GA"/"MI" are US states and must not be waved through, because
 * a confidently-wrong tax warning is worse than no warning at all.
 *
 * NOTE: `apps/rest_server/dao/rma-v4.js` carries a looser local variant of this
 * (it does pass any 2-letter value through). It cannot import this package —
 * rest_server is CommonJS and `@atek/ca-tax` is ESM-only — so the two are not
 * unified here.
 */
export function normalizeProvince(value) {
    if (value === null || value === undefined)
        return null;
    const raw = String(value).trim();
    if (!raw)
        return null;
    // 1. Already a code ("QC", "qc", "Qc").
    const upper = raw.toUpperCase();
    if (PROVINCE_CODES.has(upper))
        return upper;
    // 2. A full name in either language, in any case/accent spelling.
    const name = fold(raw);
    if (Object.hasOwn(PROVINCE_NAMES, name))
        return PROVINCE_NAMES[name];
    // 3. A code prefixing the name ("QC - Québec"), as entered by some operators.
    const prefixed = /^([A-Za-z]{2})\s*[-–—/,:|]/.exec(raw);
    if (prefixed && PROVINCE_CODES.has(prefixed[1].toUpperCase())) {
        return prefixed[1].toUpperCase();
    }
    return null;
}
//# sourceMappingURL=province.js.map